import React, { useState } from 'react';
import { Card, List, Typography, Input, Button, Space, Empty, Tooltip } from 'antd';
import { EnvironmentOutlined, NodeIndexOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { LogEntry } from '../../types/triage';

dayjs.extend(relativeTime);

interface LogCardProps {
  title: string;
  icon: React.ReactNode;
  logs: LogEntry[];
  onAddLog: (text: string) => void;
  disabled?: boolean;
}

const LogCard: React.FC<LogCardProps> = ({ title, icon, logs, onAddLog, disabled }) => {
  const [newText, setNewText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newText.trim()) return;
    setSubmitting(true);
    await onAddLog(newText.trim());
    setNewText('');
    setSubmitting(false);
  };

  return (
    <Card
      title={<Space>{icon}<span>{title}</span></Space>}
      size="small"
      style={{ height: '100%' }}
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {logs.length} entries
        </Typography.Text>
      }
    >
      <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 12 }}>
        {logs.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No logs yet" />
        ) : (
          <List
            dataSource={logs}
            size="small"
            renderItem={(log) => (
              <List.Item style={{ padding: '4px 0' }}>
                <div>
                  <Typography.Text style={{ fontSize: 13 }}>{log.log_text}</Typography.Text>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {log.created_by_name || log.created_by} -- {dayjs(log.created_at).fromNow()}
                    </Typography.Text>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </div>
      <Input.TextArea
        placeholder={disabled ? 'No permission to add logs' : 'Add a log entry...'}
        autoSize={{ minRows: 1, maxRows: 3 }}
        value={newText}
        onChange={(e) => setNewText(e.target.value)}
        onPressEnter={(e) => {
          if (!e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
        style={{ marginBottom: 8 }}
        disabled={disabled}
      />
      <Tooltip title={disabled ? 'You do not have permission to add logs' : undefined}>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={handleSubmit}
          loading={submitting}
          disabled={disabled || !newText.trim()}
        >
          Add Log
        </Button>
      </Tooltip>
    </Card>
  );
};

interface LogsSectionProps {
  locationLogs: LogEntry[];
  nodeLogs: LogEntry[];
  onAddLocationLog: (text: string) => void;
  onAddNodeLog: (text: string) => void;
}

const LogsSection: React.FC<LogsSectionProps> = ({
  locationLogs,
  nodeLogs,
  onAddLocationLog,
  onAddNodeLog,
}) => {
  // Flex-wrap instead of antd Row/Col — see InfoCards.tsx for why: this
  // pane's actual width doesn't track the viewport breakpoints Col uses.
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
      <div style={{ flex: '1 1 300px', minWidth: 0 }}>
        <LogCard
          title="Location Logs"
          icon={<EnvironmentOutlined />}
          logs={locationLogs}
          onAddLog={onAddLocationLog}
        />
      </div>
      <div style={{ flex: '1 1 300px', minWidth: 0 }}>
        <LogCard
          title="Node Logs"
          icon={<NodeIndexOutlined />}
          logs={nodeLogs}
          onAddLog={onAddNodeLog}
        />
      </div>
    </div>
  );
};

export default LogsSection;
